import type { TFunction } from "i18next";

import { shouldShowScriptRationale } from "@/features/opportunities/lib/original-brief-url";
import type { PersonaScriptDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";

import { getPersonaScriptDisplayTitle } from "./script-display-title.js";

export interface ScriptHookCopyItem {
  position: number;
  voiceover: string;
  onScreenText: string | null;
}

/** Public preview URL — token is the script's own id, same as Opportunities. */
export function buildPersonaScriptShareUrl(scriptId: string, origin: string): string {
  return `${origin}/s/${scriptId}`;
}

interface ScriptHookBlockLabels {
  title: string;
  primaryHook: string;
  hook: string;
  voiceover: string;
  onScreenText: string;
}

/** English hook headings. Copy and export share these; the public page translates via `t`. */
const HOOK_BLOCK_LABELS: ScriptHookBlockLabels = {
  title: "Scene 1 hooks",
  primaryHook: "Hook {{index}} (primary)",
  hook: "Hook {{index}}",
  voiceover: "Voiceover",
  onScreenText: "On-screen text",
};

function applyHookIndex(template: string, index: number): string {
  return template.replaceAll("{{index}}", String(index));
}

/**
 * Scene 1 hooks block, primary first and marked. Empty for a one-hook script so
 * its copy stays exactly as before alternates existed.
 */
function formatScriptHooksLines(
  hooks: readonly ScriptHookCopyItem[],
  labels: ScriptHookBlockLabels,
): string[] {
  if (hooks.length <= 1) return [];
  const lines = [labels.title];
  const ordered = [...hooks].sort((a, b) => a.position - b.position);
  ordered.forEach((hook, index) => {
    lines.push(applyHookIndex(index === 0 ? labels.primaryHook : labels.hook, index + 1));
    lines.push(`  ${labels.voiceover}: ${hook.voiceover.trim()}`);
    if (hook.onScreenText?.trim()) {
      lines.push(`  ${labels.onScreenText}: ${hook.onScreenText.trim()}`);
    }
  });
  return lines;
}

/** Translated hooks block for the public preview. */
export function formatScriptHooksBlock(hooks: readonly ScriptHookCopyItem[], t: TFunction): string[] {
  if (hooks.length <= 1) return [];
  const voiceoverLabel = t("personaOpportunities.scripts.hooksBlock.voiceover", "Voiceover");
  const onScreenTextLabel = t("personaOpportunities.scripts.hooksBlock.onScreenText", "On-screen text");
  const lines = [t("personaOpportunities.scripts.hooksBlock.title", "Scene 1 hooks")];

  const ordered = [...hooks].sort((a, b) => a.position - b.position);
  ordered.forEach((hook, index) => {
    lines.push(
      index === 0
        ? t("personaOpportunities.scripts.hooksBlock.primaryHook", {
            defaultValue: "Hook {{index}} (primary)",
            index: index + 1,
          })
        : t("personaOpportunities.scripts.hooksBlock.hook", {
            defaultValue: "Hook {{index}}",
            index: index + 1,
          }),
    );
    lines.push(`  ${voiceoverLabel}: ${hook.voiceover.trim()}`);
    if (hook.onScreenText?.trim()) {
      lines.push(`  ${onScreenTextLabel}: ${hook.onScreenText.trim()}`);
    }
  });
  return lines;
}

/** Field labels for the shared script dump. Copy says "Rationale"; Word says "Why this works". */
export interface ScriptDocumentLabels {
  sequence: string;
  concept: string;
  duration: string;
  presenter: string;
  background: string;
  product: string;
  scene: string;
  seeing: string;
  hearing: string;
  props: string;
  rationale: string;
}

export const COPY_SCRIPT_LABELS: ScriptDocumentLabels = {
  sequence: "Sequence",
  concept: "Concept",
  duration: "Duration",
  presenter: "Presenter",
  background: "Background",
  product: "Product",
  scene: "Scene",
  seeing: "Seeing",
  hearing: "Hearing",
  props: "Props",
  rationale: "Rationale",
};

/**
 * One script dump for copy, .txt, and .docx. Callers pass the labels and share
 * the on-screen title rule, so a missing hook cannot drift between formats.
 */
export function formatPersonaScriptDocument(
  script: PersonaScriptDto,
  untitled: string,
  labels: ScriptDocumentLabels = COPY_SCRIPT_LABELS,
): string {
  const lines: string[] = [];
  lines.push(getPersonaScriptDisplayTitle(script, untitled));
  lines.push("");

  if (script.adDescription?.trim()) {
    lines.push(script.adDescription.trim());
    lines.push("");
  }

  const sequence =
    script.scenes.length > 0
      ? script.scenes.map((scene) => scene.sceneName).join(" → ")
      : script.sequence?.trim() || null;
  if (sequence) lines.push(`${labels.sequence}: ${sequence}`);
  if (script.concept?.trim()) lines.push(`${labels.concept}: ${script.concept.trim()}`);
  if (script.duration?.trim()) lines.push(`${labels.duration}: ${script.duration.trim()}`);
  if (script.presenter?.trim()) lines.push(`${labels.presenter}: ${script.presenter.trim()}`);
  if (script.background?.trim()) lines.push(`${labels.background}: ${script.background.trim()}`);
  if (script.product?.trim()) lines.push(`${labels.product}: ${script.product.trim()}`);
  if (
    sequence ||
    script.concept?.trim() ||
    script.duration?.trim() ||
    script.presenter?.trim() ||
    script.background?.trim() ||
    script.product?.trim()
  ) {
    lines.push("");
  }

  for (const scene of script.scenes) {
    lines.push(`${labels.scene} ${scene.sceneNumber} — ${scene.sceneName}`);
    lines.push(`  ${labels.seeing}: ${scene.seeing}`);
    lines.push(`  ${labels.hearing}: ${scene.hearing}`);
    if (scene.product) lines.push(`  ${labels.product}: ${scene.product}`);
    if (scene.props) lines.push(`  ${labels.props}: ${scene.props}`);
  }
  if (script.scenes.length > 0) lines.push("");

  if (script.script?.trim()) {
    lines.push(script.script.trim());
  }

  const hooksBlock = formatScriptHooksLines(script.hooks ?? [], HOOK_BLOCK_LABELS);
  if (hooksBlock.length > 0) {
    if (lines.at(-1) !== "") lines.push("");
    lines.push(...hooksBlock);
  }

  const rationale = script.rationale?.trim();
  if (rationale && shouldShowScriptRationale(script)) {
    if (lines.at(-1) !== "") lines.push("");
    lines.push(`${labels.rationale}: ${rationale}`);
  }

  return lines.join("\n").trimEnd();
}

/** Plain-text script dump for clipboard. */
export function formatPersonaScriptForCopy(script: PersonaScriptDto, untitled: string): string {
  return formatPersonaScriptDocument(script, untitled, COPY_SCRIPT_LABELS);
}
